import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import Link from 'next/link';

import * as Styled from './OrderPreview.styled';
import {
  convertGlobalIdToOrderId,
  formatOrderName,
  isDateMoreThanMonthsOld
} from '../../Account.utils';
import { getProductVariantUrl } from '../../../../util/util';
import { vercel_base_url } from '../../../../util/config';
import { BUILD } from '../../../../util/const';
import ErrorBoundary from '../../../ErrorBoundary/ErrorBoundary';
import { ORDER_INFO } from './OrderPreview.gql';
import { GET_USER_LOCALE } from '../../../App.gql';
import OrderStatusPill from '../OrderStatusPill/OrderStatusPill';
import { formatPrice } from '../../../../util/localize';
/**
 * Displays basic information about an order.
 */
const OrderPreview = ({
  allItems,
  successfulFulfillments,
  fulfillmentStatus,
  financialStatus,
  canceledAt,
  name,
  currencyCode,
  totalPrice,
  processedAt,
  recipientName,
  orderDetailsLink,
  onRequestClose,
  withReactRouter,
  isLoading
}) => {
  const base_url = BUILD === 'shopify' ? vercel_base_url : '';
  const LinkComponent = BUILD === 'shopify' ? 'a' : Link;
  const shouldDisplayTrackButton =
    !!successfulFulfillments.length &&
    !!successfulFulfillments[0].trackingInfo?.length &&
    !isDateMoreThanMonthsOld(processedAt, 3);

  return (
    <Styled.InformationCard
      header={
        <Styled.OrderPreviewHeader>
          <Styled.OrderNumberWrapper>
            <Styled.OrderNumber isLoading={isLoading}>
              {name && `Order ${formatOrderName(name)}`}
            </Styled.OrderNumber>
            {isLoading ? (
              <Styled.LoadingPill />
            ) : (
              <OrderStatusPill
                financialStatus={financialStatus}
                fulfillmentStatus={fulfillmentStatus}
                canceledAt={canceledAt}
              />
            )}
          </Styled.OrderNumberWrapper>
          <Styled.Date isLoading={isLoading}>
            {processedAt &&
              `Placed on ${new Intl.DateTimeFormat([], {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }).format(new Date(processedAt))}`}
          </Styled.Date>
          <Styled.Identifiers isLoading={isLoading}>
            {recipientName && (
              <Styled.Identifier>
                Shipped To<span>{recipientName}</span>
              </Styled.Identifier>
            )}
            {totalPrice && (
              <Styled.Identifier>
                Total Price
                <span>{formatPrice(totalPrice, currencyCode)}</span>
              </Styled.Identifier>
            )}
            {!!allItems.length && (
              <Styled.Identifier>
                Items<span>{allItems.length}</span>
              </Styled.Identifier>
            )}
          </Styled.Identifiers>
          {shouldDisplayTrackButton && (
            <Styled.TrackButton
              isFull
              as="a"
              size="medium"
              variant="defaultDark"
              href={successfulFulfillments[0].trackingInfo[0]?.url}
              target="_blank"
            >
              Track Order
            </Styled.TrackButton>
          )}

          {isLoading ? (
            <Styled.LoadingView />
          ) : (
            <Styled.Link
              as={!withReactRouter ? LinkComponent : undefined}
              href={`${base_url}${orderDetailsLink}`}
              to={orderDetailsLink}
              key={orderDetailsLink}
              path={orderDetailsLink}
              onClick={onRequestClose}
            >
              <Styled.Button
                forwardedAs="span"
                size="medium"
                variant="secondary"
                isFull
              >
                View Order Details
              </Styled.Button>
            </Styled.Link>
          )}
        </Styled.OrderPreviewHeader>
      }
    >
      {successfulFulfillments.length ? (
        <>
          {successfulFulfillments.map((shipment, index) => (
            <Styled.Fulfillment key={index}>
              {successfulFulfillments.length > 1 && (
                <Styled.FulfillmentHeader>
                  <span>Shipment {index + 1}</span>
                </Styled.FulfillmentHeader>
              )}
              {shipment.fulfillmentLineItems.edges.map((item, idx) => (
                <Styled.ProductVariant
                  key={idx}
                  title={item.node.lineItem.title}
                  subtitle={item.node.lineItem.variant?.title}
                  image={item.node.lineItem.variant?.image?.originalSrc}
                  productVariantUrl={getProductVariantUrl(
                    item.node.lineItem.variant
                  )}
                />
              ))}
            </Styled.Fulfillment>
          ))}
          {fulfillmentStatus !== 'FULFILLED' && !canceledAt && (
            <Styled.FulfillmentDisclaimer>
              Additional items from your order are being prepared.
            </Styled.FulfillmentDisclaimer>
          )}
        </>
      ) : (
        allItems.map((item, idx) => (
          <Styled.ProductVariant
            key={idx}
            title={item.node.title}
            subtitle={item.node.variant?.title}
            image={item.node.variant?.image?.originalSrc}
            productVariantUrl={getProductVariantUrl(item.node.variant)}
          />
        ))
      )}

      {isLoading &&
        [...Array(3)].map((_item, idx) => (
          <Styled.ProductVariant key={idx} isLoading={isLoading} />
        ))}
    </Styled.InformationCard>
  );
};

const OrderPreviewWithData = ({ orderId, onRequestClose, withReactRouter }) => {
  const { data: localeData } = useQuery(GET_USER_LOCALE);
  const country = localeData?.user?.locale?.country;
  const { error, loading, data } = useQuery(ORDER_INFO, {
    context: { service: 'storefront' },
    variables: { orderId, country },
    skip: !country
  });

  if (error) return null;

  if (loading) return <OrderPreview isLoading />;

  const totalPrice = data.node?.totalPriceV2.amount;
  const allItems = data.node?.lineItems.edges;
  const recipientName = data.node?.shippingAddress
    ? `${data.node?.shippingAddress.firstName} ${data.node?.shippingAddress.lastName}`
    : null;
  const orderDetailsLink =
    data.node &&
    `/account/order-history/${convertGlobalIdToOrderId(data.node?.id)}`;

  return (
    <ErrorBoundary>
      <OrderPreview
        {...data.node}
        totalPrice={totalPrice}
        allItems={allItems}
        recipientName={recipientName}
        orderDetailsLink={orderDetailsLink}
        onRequestClose={onRequestClose}
        withReactRouter={withReactRouter}
        isLoading={loading}
      />
    </ErrorBoundary>
  );
};

OrderPreviewWithData.propTypes = {
  /**
   * Order ID
   */
  orderId: PropTypes.string.isRequired,
  /**
   * A function that is called when `AccountNavDialog` is requesting to close.
   */
  onRequestClose: PropTypes.func,
  /**
   * `true` to render react router links
   *  when the component is rendered within a SPA such as Account
   *  otherwise render a normal anchor tag to navigate to the SPA
   */
  withReactRouter: PropTypes.bool
};

OrderPreviewWithData.defaultProps = {
  onRequestClose: undefined,
  withReactRouter: false
};

OrderPreview.propTypes = {
  /**
   * unique identifier for an order
   */
  allItems: PropTypes.arrayOf(PropTypes.shape({})),
  successfulFulfillments: PropTypes.arrayOf(
    PropTypes.shape({
      trackingInfo: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string
        })
      )
    })
  ),
  /**
   * Order Name / Number
   */
  name: PropTypes.string,
  currencyCode: PropTypes.string.isRequired,
  totalPrice: PropTypes.string,
  financialStatus: PropTypes.string,
  fulfillmentStatus: PropTypes.oneOf([
    'FULFILLED',
    'IN_PROGRESS',
    'OPEN',
    'PARTIALLY_FULFILLED',
    'PENDING_FULFILLMENT',
    'RESTOCKED',
    'SCHEDULED',
    'UNFULFILLED'
  ]),
  canceledAt: PropTypes.oneOfType([
    (props, propName, componentName) => {
      if (
        !/^\d{4}(-\d\d(-\d\d(T\d\d:\d\d(:\d\d)?(\.\d+)?(([+-]\d\d:\d\d)|Z)?)?)?)?$/i.test(
          props[propName]
        )
      ) {
        return new Error(
          `Invalid prop \`${propName}\` passed to \`${componentName}\`. Expected a ISO8601 string.`
        );
      }
    }
  ]),
  processedAt: PropTypes.oneOfType([
    (props, propName, componentName) => {
      if (
        !/^\d{4}(-\d\d(-\d\d(T\d\d:\d\d(:\d\d)?(\.\d+)?(([+-]\d\d:\d\d)|Z)?)?)?)?$/i.test(
          props[propName]
        )
      ) {
        return new Error(
          `Invalid prop \`${propName}\` passed to \`${componentName}\`. Expected a ISO8601 string.`
        );
      }
    }
  ]),
  recipientName: PropTypes.string,
  orderDetailsLink: PropTypes.string,
  /**
   * A function that is called when `AccountNavDialog` is requesting to close.
   */
  onRequestClose: PropTypes.func,
  /**
   * `true` to render react router links
   *  when the component is rendered within a SPA such as Account
   *  otherwise render a normal anchor tag to navigate to the SPA
   */
  withReactRouter: PropTypes.bool,
  /**
   * `true` to render loading state of OrderPreview (when data is being fetched)
   */
  isLoading: PropTypes.bool
};

OrderPreview.defaultProps = {
  allItems: [],
  recipientName: null,
  canceledAt: null,
  successfulFulfillments: [],
  financialStatus: null,
  orderDetailsLink: '/account/order-history',
  onRequestClose: undefined,
  withReactRouter: false,
  name: null,
  totalPrice: null,
  processedAt: null,
  fulfillmentStatus: null
};

export { OrderPreview };
export default OrderPreviewWithData;
